(() => {
  if (window.FibroluxCutPlanPdf) return;

  const scriptLoads = new Map();
  const ORANGE = [0.949, 0.455, 0.0];
  const NAVY = [0.075, 0.122, 0.157];
  const SLATE = [0.188, 0.235, 0.263];
  const MID = [0.43, 0.47, 0.49];
  const LIGHT = [0.956, 0.949, 0.929];
  const LINE = [0.79, 0.80, 0.79];
  const WHITE = [1, 1, 1];

  const rgbColor = (PDFLib, values) => PDFLib.rgb(values[0], values[1], values[2]);
  const clean = (value) =>
    String(value ?? '')
      .replace(/[–—]/g, '-')
      .replace(/→/g, '->')
      .replace(/×/g, 'x')
      .replace(/ẞ/g, 'SS')
      .replace(/\u00a0/g, ' ')
      .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '');
  const upper = (value) => clean(value).toUpperCase().replace(/ẞ/g, 'SS');
  const number = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const int = (value) => Math.round(number(value));
  const percent = (value) => `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(number(value))} %`;
  const money = (value, language = 'de') =>
    new Intl.NumberFormat(language || 'de', { style: 'currency', currency: 'EUR' }).format(number(value) / 100);
  const safeFileName = (value) => clean(value).replace(/[^a-z0-9._-]+/gi, '-').replace(/-+/g, '-');

  function loadScript(url) {
    if (!url) return Promise.reject(new Error('PDF-Skript fehlt'));
    if (scriptLoads.has(url)) return scriptLoads.get(url);
    const promise = new Promise((resolve, reject) => {
      const existing = Array.from(document.scripts).find((script) => script.src === url);
      if (existing) {
        if (existing.dataset.loaded === 'true') resolve();
        else {
          existing.addEventListener('load', resolve, { once: true });
          existing.addEventListener('error', reject, { once: true });
        }
        return;
      }
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', () => reject(new Error('PDF-Skript konnte nicht geladen werden')), { once: true });
      document.head.appendChild(script);
    });
    scriptLoads.set(url, promise);
    return promise;
  }

  async function ensurePdfLib(url) {
    if (window.PDFLib?.PDFDocument) return window.PDFLib;
    await loadScript(url);
    if (!window.PDFLib?.PDFDocument) throw new Error('PDF-Bibliothek ist nicht verfügbar');
    return window.PDFLib;
  }

  function wrap(font, text, size, maxWidth) {
    const paragraphs = clean(text).split(/\r?\n/);
    const lines = [];
    paragraphs.forEach((paragraph, paragraphIndex) => {
      const words = paragraph.split(/\s+/).filter(Boolean);
      if (!words.length) {
        lines.push('');
        return;
      }
      let line = '';
      words.forEach((word) => {
        const candidate = line ? `${line} ${word}` : word;
        if (line && font.widthOfTextAtSize(candidate, size) > maxWidth) {
          lines.push(line);
          line = word;
        } else {
          line = candidate;
        }
      });
      if (line) lines.push(line);
      if (paragraphIndex < paragraphs.length - 1 && paragraph) lines.push('');
    });
    return lines;
  }

  function drawWrapped(page, font, text, options) {
    const { x, y, size = 10, maxWidth, color, lineHeight = size * 1.35, maxLines = 99 } = options;
    const lines = wrap(font, text, size, maxWidth).slice(0, maxLines);
    lines.forEach((line, index) => page.drawText(line, { x, y: y - index * lineHeight, size, font, color }));
    return y - lines.length * lineHeight;
  }

  async function createDocument(pdfLibraryUrl) {
    const PDFLib = await ensurePdfLib(pdfLibraryUrl);
    const doc = await PDFLib.PDFDocument.create();
    doc.setTitle('FIBROLUX Zuschnittsplan');
    doc.setAuthor('FIBROLUX');
    doc.setCreator('FIBROLUX Zuschnittskonfigurator');
    doc.setProducer('FIBROLUX Onlineshop');
    const regular = await doc.embedFont(PDFLib.StandardFonts.Helvetica);
    const bold = await doc.embedFont(PDFLib.StandardFonts.HelveticaBold);
    return { PDFLib, doc, regular, bold };
  }

  function addHeader(ctx, page, title, planId, subtitle = '') {
    const { PDFLib, bold, regular } = ctx;
    const { width, height } = page.getSize();
    page.drawRectangle({ x: 0, y: height - 88, width, height: 88, color: rgbColor(PDFLib, NAVY) });
    page.drawText('FIBROLUX', { x: 40, y: height - 43, size: 23, font: bold, color: rgbColor(PDFLib, ORANGE) });
    page.drawText(clean(title), { x: 40, y: height - 70, size: 12, font: bold, color: rgbColor(PDFLib, WHITE) });
    const idText = `PLAN-ID  ${clean(planId)}`;
    page.drawText(idText, {
      x: width - 40 - regular.widthOfTextAtSize(idText, 8),
      y: height - 40,
      size: 8,
      font: regular,
      color: rgbColor(PDFLib, WHITE),
    });
    if (subtitle) {
      const subtitleText = clean(subtitle);
      page.drawText(subtitleText, {
        x: width - 40 - regular.widthOfTextAtSize(subtitleText, 8),
        y: height - 57,
        size: 8,
        font: regular,
        color: rgbColor(PDFLib, LINE),
      });
    }
  }

  function addFooter(ctx, page, pageNumber, totalPages) {
    const { PDFLib, regular } = ctx;
    const { width } = page.getSize();
    page.drawLine({ start: { x: 40, y: 32 }, end: { x: width - 40, y: 32 }, thickness: 0.6, color: rgbColor(PDFLib, LINE) });
    page.drawText('FIBROLUX GmbH | Verlege- und Zuschnittsunterlage zur Bestellung', {
      x: 40, y: 18, size: 7, font: regular, color: rgbColor(PDFLib, MID),
    });
    const marker = `${pageNumber} / ${totalPages}`;
    page.drawText(marker, { x: width - 40 - regular.widthOfTextAtSize(marker, 7), y: 18, size: 7, font: regular, color: rgbColor(PDFLib, MID) });
  }

  function drawSectionTitle(ctx, page, title, y) {
    const { PDFLib, bold } = ctx;
    page.drawRectangle({ x: 40, y: y - 2, width: 3, height: 14, color: rgbColor(PDFLib, ORANGE) });
    page.drawText(upper(title), { x: 51, y, size: 10, font: bold, color: rgbColor(PDFLib, NAVY) });
    return y - 24;
  }

  function drawMetrics(ctx, page, metrics, y, width) {
    const { PDFLib, bold, regular } = ctx;
    const gap = 8;
    const boxWidth = (width - gap * (metrics.length - 1)) / metrics.length;
    metrics.forEach((metric, index) => {
      const x = 40 + index * (boxWidth + gap);
      page.drawRectangle({ x, y: y - 48, width: boxWidth, height: 48, color: rgbColor(PDFLib, LIGHT), borderColor: rgbColor(PDFLib, LINE), borderWidth: 0.5 });
      page.drawText(upper(metric.label), { x: x + 10, y: y - 15, size: 6.5, font: bold, color: rgbColor(PDFLib, MID) });
      drawWrapped(page, bold, metric.value, { x: x + 10, y: y - 33, size: 10, maxWidth: boxWidth - 20, color: rgbColor(PDFLib, NAVY), lineHeight: 11, maxLines: 1 });
    });
    return y - 62;
  }

  function drawKeyValueRows(ctx, page, rows, y, width) {
    const { PDFLib, bold, regular } = ctx;
    rows.filter((row) => row?.value !== undefined && row?.value !== null && String(row.value) !== '').forEach((row) => {
      page.drawLine({ start: { x: 40, y: y - 7 }, end: { x: 40 + width, y: y - 7 }, thickness: 0.4, color: rgbColor(PDFLib, LINE) });
      page.drawText(clean(row.label), { x: 40, y: y - 22, size: 8, font: bold, color: rgbColor(PDFLib, MID) });
      drawWrapped(page, regular, row.value, { x: 166, y: y - 22, size: 9, maxWidth: width - 126, color: rgbColor(PDFLib, NAVY), lineHeight: 12, maxLines: 3 });
      y -= Math.max(35, wrap(regular, row.value, 9, width - 126).slice(0, 3).length * 12 + 19);
    });
    return y;
  }

  function finalize(ctx) {
    const pages = ctx.doc.getPages();
    pages.forEach((page, index) => addFooter(ctx, page, index + 1, pages.length));
  }

  function groupProfileBars(bars) {
    const groups = [];
    const bySignature = new Map();
    (Array.isArray(bars) ? bars : []).forEach((bar) => {
      const pieces = (Array.isArray(bar?.pieces) ? bar.pieces : []).map(int);
      const signature = `${bar?.variant?.id || ''}|${int(bar?.variant?.length)}|${pieces.join(',')}|${int(bar?.rest)}`;
      const quantity = Math.max(1, int(bar?.quantity || 1));
      if (bySignature.has(signature)) bySignature.get(signature).quantity += quantity;
      else {
        const group = { bar, quantity };
        bySignature.set(signature, group);
        groups.push(group);
      }
    });
    return groups;
  }

  async function createProfilePdf({ result, planId, language = 'de', kerf = 0, pdfLibraryUrl }) {
    const ctx = await createDocument(pdfLibraryUrl);
    const { PDFLib, doc, regular, bold } = ctx;
    const cover = doc.addPage(PDFLib.PageSizes.A4);
    addHeader(ctx, cover, 'PROFIL-ZUSCHNITTSAUFTRAG', planId, 'Bedarfs- und Sägeschnittplan');
    let y = 718;
    cover.drawText('Ihr Zuschnitt auf einen Blick', { x: 40, y, size: 22, font: bold, color: rgbColor(PDFLib, NAVY) });
    y -= 27;
    y = drawWrapped(cover, regular, 'Diese Unterlage gehört zu Ihrem Warenkorbauftrag. Sie fasst das Rohmaterial, die bestellten Fertigmaße und die Verteilung auf die Profile zusammen.', {
      x: 40, y, size: 9, maxWidth: 515, color: rgbColor(PDFLib, MID), lineHeight: 13,
    }) - 18;
    y = drawMetrics(ctx, cover, [
      { label: 'Fertigteile', value: String(int(result?.piecesLength || 0)) },
      { label: 'Rohprofile', value: String((result?.bars || []).reduce((sum, bar) => sum + Math.max(1, int(bar?.quantity || 1)), 0)) },
      { label: 'Schnitte', value: String(int(result?.cutCount || 0)) },
      { label: 'Gesamtpreis', value: money(result?.totalPrice || 0, language) },
    ], y, 515);
    y = drawSectionTitle(ctx, cover, 'Auftrag', y);
    const options = (result?.selectedOptions || []).map((option) => `${option.name}: ${option.value}`).join(' | ');
    const cuts = (result?.cuts || []).map((cut) => `${Math.max(1, int(cut.quantity || 1))}x ${int(cut.length)} mm`).join(', ');
    const raw = groupProfileBars(result?.bars || []).map((group) => `${group.quantity}x ${int(group.bar?.variant?.length)} mm`).join(', ');
    y = drawKeyValueRows(ctx, cover, [
      { label: 'Artikel', value: result?.profile?.title || 'Profil' },
      { label: 'Ausführung', value: options || '-' },
      { label: 'Bestellmaße', value: cuts || '-' },
      { label: 'Rohmaterial', value: raw || '-' },
      { label: 'Schnittfuge', value: `${int(kerf)} mm` },
      { label: 'Verschnitt', value: percent(result?.wastePercent || 0) },
    ], y, 515);
    y -= 8;
    y = drawSectionTitle(ctx, cover, 'Wichtiger Hinweis', y);
    drawWrapped(cover, regular, 'Maße und Stückzahlen werden gemäß diesem Plan gefertigt. Bitte prüfen Sie die Angaben vor Abschluss der Bestellung. Produktions- und Sägetoleranzen sowie die technische Eignung für den vorgesehenen Einsatz bleiben zu berücksichtigen.', {
      x: 40, y, size: 8.5, maxWidth: 515, color: rgbColor(PDFLib, MID), lineHeight: 12,
    });

    const groups = groupProfileBars(result?.bars || []);
    let page = null;
    let planY = 0;
    groups.forEach((group, index) => {
      if (!page || planY < 160) {
        page = doc.addPage(PDFLib.PageSizes.A4);
        addHeader(ctx, page, 'PROFIL-ZUSCHNITTSPLAN', planId, `Sägeplan ${Math.floor(index / 4) + 1}`);
        planY = 718;
      }
      const bar = group.bar || {};
      const rawLength = Math.max(1, number(bar?.variant?.length));
      const pieces = (Array.isArray(bar?.pieces) ? bar.pieces : []).map(number).filter((value) => value > 0);
      page.drawText(`${group.quantity}x Rohprofil ${int(rawLength)} mm`, { x: 40, y: planY, size: 11, font: bold, color: rgbColor(PDFLib, NAVY) });
      page.drawText(`Plan ${index + 1}`, { x: 500, y: planY, size: 8, font: bold, color: rgbColor(PDFLib, ORANGE) });
      const barX = 40;
      const barY = planY - 65;
      const barWidth = 515;
      const barHeight = 43;
      page.drawRectangle({ x: barX, y: barY, width: barWidth, height: barHeight, color: rgbColor(PDFLib, LIGHT), borderColor: rgbColor(PDFLib, NAVY), borderWidth: 0.8 });
      let cursor = barX;
      pieces.forEach((piece, pieceIndex) => {
        const segmentWidth = Math.max(1.5, (piece / rawLength) * barWidth);
        page.drawRectangle({ x: cursor, y: barY, width: Math.min(segmentWidth, barX + barWidth - cursor), height: barHeight, color: rgbColor(PDFLib, pieceIndex % 2 ? [0.96, 0.58, 0.12] : ORANGE), borderColor: rgbColor(PDFLib, WHITE), borderWidth: 0.7 });
        const label = `${int(piece)} mm`;
        if (segmentWidth > regular.widthOfTextAtSize(label, 7) + 8) {
          page.drawText(label, { x: cursor + (segmentWidth - regular.widthOfTextAtSize(label, 7)) / 2, y: barY + 18, size: 7, font: bold, color: rgbColor(PDFLib, WHITE) });
        }
        cursor += segmentWidth;
      });
      const rest = Math.max(0, int(bar?.rest || 0));
      page.drawText(`Fertigteile: ${pieces.map((piece) => `${int(piece)} mm`).join(' | ')}`, { x: 40, y: barY - 17, size: 8, font: regular, color: rgbColor(PDFLib, MID) });
      page.drawText(`Rest: ${rest} mm  |  Schnittverlust: ${int(bar?.kerfLoss || 0)} mm je Rohprofil`, { x: 40, y: barY - 31, size: 8, font: regular, color: rgbColor(PDFLib, MID) });
      planY -= 138;
    });

    finalize(ctx);
    const bytes = await doc.save();
    return new Blob([bytes], { type: 'application/pdf' });
  }

  function collectPlatePieces(plate) {
    return (Array.isArray(plate?.rows) ? plate.rows : []).flatMap((row) => (Array.isArray(row?.pieces) ? row.pieces : []));
  }

  function groupPlates(plates) {
    const groups = [];
    const bySignature = new Map();
    (Array.isArray(plates) ? plates : []).forEach((plate) => {
      const variant = plate?.variant || {};
      const pieces = collectPlatePieces(plate).map((piece) => [int(piece.x), int(piece.y), int(piece.length), int(piece.width), piece.rotated ? 1 : 0].join(',')).sort().join('|');
      const signature = `${variant.id || ''}|${int(variant.length)}x${int(variant.width)}|${pieces}`;
      if (bySignature.has(signature)) bySignature.get(signature).quantity += 1;
      else {
        const group = { plate, quantity: 1 };
        bySignature.set(signature, group);
        groups.push(group);
      }
    });
    return groups;
  }

  async function createGratingPdf({ result, planId, language = 'de', pdfLibraryUrl }) {
    const ctx = await createDocument(pdfLibraryUrl);
    const { PDFLib, doc, regular, bold } = ctx;
    const cover = doc.addPage(PDFLib.PageSizes.A4);
    addHeader(ctx, cover, 'GITTERROST-ZUSCHNITTAUFTRAG', planId, 'Bedarfs- und Plattenzuschnittplan');
    let y = 718;
    cover.drawText('Ihr Zuschnitt auf einen Blick', { x: 40, y, size: 22, font: bold, color: rgbColor(PDFLib, NAVY) });
    y -= 27;
    y = drawWrapped(cover, regular, 'Diese Unterlage gehört zu Ihrem Warenkorbauftrag. Sie zeigt die bestellten Fertigmaße und deren Position auf jeder Rohplatte.', {
      x: 40, y, size: 9, maxWidth: 515, color: rgbColor(PDFLib, MID), lineHeight: 13,
    }) - 18;
    y = drawMetrics(ctx, cover, [
      { label: 'Fertigteile', value: String(int(result?.totalPieces || 0)) },
      { label: 'Rohplatten', value: String(int(result?.plateCount || (result?.plates || []).length)) },
      { label: 'Schnitte', value: String(int(result?.totalCuts || 0)) },
      { label: 'Gesamtpreis', value: money(result?.totalPrice || 0, language) },
    ], y, 515);
    y = drawSectionTitle(ctx, cover, 'Auftrag', y);
    const options = Object.entries(result?.selectedValues || {})
      .map(([index, value]) => `${result?.product?.optionNames?.[Number(index)] || index}: ${value}`)
      .join(' | ');
    const desired = (result?.desiredCuts || []).map((cut) => `${Math.max(1, int(cut.quantity || 1))}x ${int(cut.length)}x${int(cut.width)} mm`).join(', ');
    const raw = (result?.variantCounts || []).length
      ? result.variantCounts.map((entry) => `${Math.max(1, int(entry.quantity))}x ${int(entry.variant?.length)}x${int(entry.variant?.width)} mm`).join(', ')
      : `${int(result?.plateCount || 0)}x ${int(result?.variant?.length)}x${int(result?.variant?.width)} mm`;
    const edgeText = result?.edgeMode === 'closed'
      ? `geschlossene Schnittkante${result?.edgeRounding ? ` (${result.edgeRounding === 'up' ? 'nächstgrößere Masche' : 'nächstkleinere Masche'})` : ''}`
      : 'offene Schnittkante';
    y = drawKeyValueRows(ctx, cover, [
      { label: 'Artikel', value: result?.product?.title || 'Gitterrost' },
      { label: 'Ausführung', value: options || '-' },
      { label: 'Bestellmaße', value: desired || '-' },
      { label: 'Rohmaterial', value: raw || '-' },
      { label: 'Schnittkante', value: edgeText },
      { label: 'Maschenweite', value: result?.meshValue || '-' },
      { label: 'Verschnitt', value: percent(result?.wastePercent || 0) },
    ], y, 515);
    y -= 8;
    y = drawSectionTitle(ctx, cover, 'Wichtiger Hinweis', y);
    drawWrapped(cover, regular, 'Maße und Stückzahlen werden gemäß diesem Plan gefertigt. Bitte prüfen Sie die Angaben vor Abschluss der Bestellung. Produktions- und Sägetoleranzen sowie die technische Eignung, Tragfähigkeit und Auflagerung bleiben zu berücksichtigen.', {
      x: 40, y, size: 8.5, maxWidth: 515, color: rgbColor(PDFLib, MID), lineHeight: 12,
    });

    const palette = [ORANGE, [0.31, 0.57, 0.62], [0.52, 0.63, 0.38], [0.58, 0.42, 0.57], [0.43, 0.48, 0.51]];
    groupPlates(result?.plates || []).forEach((group, index) => {
      const page = doc.addPage([841.89, 595.28]);
      const plate = group.plate || {};
      const variant = plate.variant || result?.variant || {};
      const rawLength = Math.max(1, number(variant.length));
      const rawWidth = Math.max(1, number(variant.width));
      addHeader(ctx, page, 'GITTERROST-ZUSCHNITTSPLAN', planId, `Rohplatte ${index + 1}${group.quantity > 1 ? ` - ${group.quantity}x identisch` : ''}`);
      page.drawText(`${group.quantity}x Rohplatte ${int(rawLength)}x${int(rawWidth)} mm`, { x: 40, y: 468, size: 14, font: bold, color: rgbColor(PDFLib, NAVY) });
      const maxDrawWidth = 750;
      const maxDrawHeight = 315;
      const scale = Math.min(maxDrawWidth / rawLength, maxDrawHeight / rawWidth);
      const plateWidth = rawLength * scale;
      const plateHeight = rawWidth * scale;
      const plateX = 40 + (maxDrawWidth - plateWidth) / 2;
      const plateY = 118 + (maxDrawHeight - plateHeight) / 2;
      page.drawRectangle({ x: plateX, y: plateY, width: plateWidth, height: plateHeight, color: rgbColor(PDFLib, LIGHT), borderColor: rgbColor(PDFLib, NAVY), borderWidth: 1 });
      collectPlatePieces(plate).forEach((piece, pieceIndex) => {
        const x = plateX + number(piece.x) * scale;
        const yFromTop = number(piece.y) * scale;
        const w = number(piece.length) * scale;
        const h = number(piece.width) * scale;
        const yPiece = plateY + plateHeight - yFromTop - h;
        page.drawRectangle({ x, y: yPiece, width: w, height: h, color: rgbColor(PDFLib, palette[pieceIndex % palette.length]), borderColor: rgbColor(PDFLib, WHITE), borderWidth: 1 });
        const label = `${int(piece.length)}x${int(piece.width)}`;
        const labelSize = w > 95 && h > 32 ? 9 : w > 55 && h > 22 ? 7 : 0;
        if (labelSize) {
          page.drawText(label, {
            x: x + Math.max(3, (w - bold.widthOfTextAtSize(label, labelSize)) / 2),
            y: yPiece + Math.max(4, (h - labelSize) / 2),
            size: labelSize,
            font: bold,
            color: rgbColor(PDFLib, WHITE),
          });
        }
      });
      page.drawText(`${int(rawLength)} mm`, { x: plateX + plateWidth / 2 - 18, y: plateY + plateHeight + 10, size: 8, font: bold, color: rgbColor(PDFLib, MID) });
      page.drawText(`${int(rawWidth)} mm`, { x: plateX + plateWidth + 8, y: plateY + plateHeight / 2, size: 8, font: bold, color: rgbColor(PDFLib, MID), rotate: PDFLib.degrees(90) });
      const piecesText = collectPlatePieces(plate).map((piece, pieceIndex) => `M${pieceIndex + 1}: ${int(piece.length)}x${int(piece.width)} mm`).join(' | ');
      drawWrapped(page, regular, piecesText || 'Keine Fertigteile', { x: 40, y: 92, size: 8, maxWidth: 750, color: rgbColor(PDFLib, MID), lineHeight: 11, maxLines: 3 });
    });

    finalize(ctx);
    const bytes = await doc.save();
    return new Blob([bytes], { type: 'application/pdf' });
  }

  async function addOrderToCartWithPdf({ cartAddUrl, items, ownerIndex, pdfBlob, fileName, ownerProperties = {} }) {
    const addUrl = String(cartAddUrl || '/cart/add').replace(/\.js$/, '') + '.js';
    const safeItems = (Array.isArray(items) ? items : []).map((item) => ({
      ...item,
      properties: { ...(item.properties || {}) },
    }));
    const safeOwnerIndex = Math.max(0, Math.min(safeItems.length - 1, int(ownerIndex)));
    const owner = safeItems[safeOwnerIndex];
    if (!owner || !pdfBlob?.size) throw new Error('PDF oder Warenkorbposition fehlt');
    Object.assign(owner.properties, ownerProperties);

    const formData = new FormData();
    formData.append('id', String(owner.id));
    formData.append('quantity', String(owner.quantity));
    Object.entries(owner.properties).forEach(([name, value]) => formData.append(`properties[${name}]`, String(value)));
    formData.append('properties[Plan-PDF]', pdfBlob, safeFileName(fileName || 'FIBROLUX-Zuschnittsplan.pdf'));

    let ownerKey = '';
    try {
      const ownerResponse = await fetch(addUrl, {
        method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: formData,
      });
      const ownerData = await ownerResponse.json().catch(() => ({}));
      if (!ownerResponse.ok) throw new Error(ownerData.description || `Warenkorbfehler ${ownerResponse.status}`);
      ownerKey = ownerData.key || '';
      if (!String(ownerData.properties?.['Plan-PDF'] || '').includes('/uploads/')) {
        throw new Error('Die Plan-PDF wurde nicht korrekt an den Auftrag angehängt');
      }

      const remainingItems = safeItems.filter((item, index) => index !== safeOwnerIndex);
      if (remainingItems.length) {
        const response = await fetch(addUrl, {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: remainingItems }),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || `Warenkorbfehler ${response.status}`);
        }
      }
      return ownerData;
    } catch (error) {
      if (ownerKey) {
        await fetch(addUrl.replace(/\/add\.js$/, '/change.js'), {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: ownerKey, quantity: 0 }),
        }).catch(() => {});
      }
      throw error;
    }
  }

  window.FibroluxCutPlanPdf = {
    ensurePdfLib,
    createProfilePdf,
    createGratingPdf,
    addOrderToCartWithPdf,
  };
})();
